import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { getCurrentUser, isAllBranchesRole } from '../utils/auth';
import { useToast } from './ToastContext';
import { useAutoSync } from '../hooks/useAutoSync';

function SalespersonSummary({ companyId }) {
  const currentUser = getCurrentUser();
  const canSelectAllBranches = isAllBranchesRole(currentUser?.role);
  
  // Toast notifications
  const { showToast } = useToast();
  
  // Auto-sync monitoring
  useAutoSync(true);

  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [salespersonData, setSalespersonData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [companyPages, setCompanyPages] = useState({});
  const [salespersonFilter, setSalespersonFilter] = useState('');
  const [salespersonStatus, setSalespersonStatus] = useState('all'); // 'all', 'active', 'inactive'
  const salespersonsPerPage = 10;

  // Branch filter
  const ALL_BRANCHES_VALUE = '';
  const [selectedBranch, setSelectedBranch] = useState(companyId ? String(companyId) : '');
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      await fetchSalespersonData();
    };
    loadData();
  }, [selectedDate, selectedBranch, salespersonStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data || []);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  };

  const getCompanyIdParam = () => {
    if (!canSelectAllBranches && !selectedBranch) return companyId || '';
    return selectedBranch === ALL_BRANCHES_VALUE ? '' : selectedBranch;
  };

  const fetchSalespersonData = async () => {
    setLoading(true);
    setCompanyPages({});
    try {
      const companyIdParam = getCompanyIdParam();
      const params = new URLSearchParams({
        date: selectedDate,
        ...(companyIdParam && { company_id: companyIdParam }),
        ...(salespersonStatus !== 'all' && { active_salesperson: salespersonStatus === 'active' ? 'true' : 'false' })
      });

      const response = await api.get(`/reports/salesperson-summary?${params}`);
      console.log('Salesperson summary data received:', response.data);
      setSalespersonData(response.data);
    } catch (err) {
      console.error('Failed to fetch salesperson summary:', err);
      setSalespersonData([]);
    } finally {
      setLoading(false);
    }
  };

  // Build dropdown list of ALL salespersons (active or inactive)
  const allSalespersons = React.useMemo(() => {
    const list = salespersonData
      .map(sp => ({ code: sp.salesperson_code, name: sp.salesperson_name, customer_count: sp.customer_count }))
      .sort((a, b) => a.name.localeCompare(b.name));
    // Remove duplicates by code
    return Array.from(new Map(list.map(item => [item.code, item])).values());
  }, [salespersonData]);

  const refreshCache = async () => {
    setLoading(true);
    setMessage('');
    try {
      const response = await api.post('/reports/salesperson-summary/refresh', { date: selectedDate });
      if (response.data && response.data.records_cached) {
        setMessage(`Cache refreshed successfully! ${response.data.records_cached} records synced.`);
      } else {
        setMessage('Cache refreshed successfully.');
      }
      await fetchSalespersonData();
    } catch (err) {
      console.error('Refresh cache error:', err.response?.data || err.message);
      setMessage(err.response?.data?.message || 'Failed to refresh cache.');
    } finally {
      setLoading(false);
    }
  };

  const toggleCompany = (companyId) => {
    if (expandedCompany === companyId) {
      setExpandedCompany(null);
    } else {
      setExpandedCompany(companyId);
      setCompanyPages(prev => ({ ...prev, [companyId]: prev[companyId] || 1 }));
    }
  };

  const setCompanyPage = (companyId, page) => {
    setCompanyPages(prev => ({ ...prev, [companyId]: page }));
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '0.00';
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '0.00';
    return Math.abs(num).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Filter by selected salesperson from dropdown
  const filteredSalespersonData = salespersonFilter
    ? salespersonData.filter(sp => sp.salesperson_name === salespersonFilter)
    : salespersonData;

  // Group data by company
  const groupedByCompany = filteredSalespersonData.reduce((acc, row) => {
    if (!acc[row.company_name]) {
      acc[row.company_name] = {
        company_id: row.company_id,
        salespersons: [],
        totals: {
          customer_count: 0,
          total_collectibles: 0,
          posted_collected: 0,
          amount_collected: 0,
          pdc: 0,
          balance_collectible: 0
        }
      };
    }

    acc[row.company_name].salespersons.push(row);
    acc[row.company_name].totals.customer_count += row.customer_count;
    acc[row.company_name].totals.total_collectibles += row.total_collectibles;
    acc[row.company_name].totals.posted_collected += row.posted_collected || 0;
    acc[row.company_name].totals.amount_collected += row.amount_collected || 0;
    acc[row.company_name].totals.pdc += row.pdc || 0;
    acc[row.company_name].totals.balance_collectible += row.balance_collectible || 0;

    return acc;
  }, {});

  const companyArray = Object.keys(groupedByCompany).map(name => ({
    name,
    ...groupedByCompany[name]
  }));

  // Calculate grand totals
  const grandTotals = companyArray.reduce((acc, company) => ({
    customer_count: acc.customer_count + company.totals.customer_count,
    total_collectibles: acc.total_collectibles + company.totals.total_collectibles,
    posted_collected: acc.posted_collected + company.totals.posted_collected,
    amount_collected: acc.amount_collected + company.totals.amount_collected,
    pdc: acc.pdc + company.totals.pdc,
    balance_collectible: acc.balance_collectible + company.totals.balance_collectible
  }), {
    customer_count: 0,
    total_collectibles: 0,
    posted_collected: 0,
    amount_collected: 0,
    pdc: 0,
    balance_collectible: 0
  });

  const cePercentage = grandTotals.total_collectibles > 0
    ? (((grandTotals.amount_collected + grandTotals.pdc) / grandTotals.total_collectibles) * 100).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="summary-container">
        <div className="loading">Loading salesperson summary...</div>
      </div>
    );
  }

  return (
    <div className="summary-container">
      {/* Page Header */}
      <div className="page-header">
        <h1>Salesperson Summary</h1>
        <button className="btn btn-primary" onClick={refreshCache} style={{ backgroundColor: '#e63740', borderColor: '#e63740' }}>
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>

      {/* Success/Error Message */}
      {message && (
        <div className={`alert ${message.includes('success') ? 'alert-success' : 'alert-error'}`}>
          {message}
        </div>
      )}

      {/* Branch, Date and Salesperson Filters */}
      <div className="card report-filters">
        {canSelectAllBranches && (
          <>
            <label htmlFor="branch-select" style={{ marginRight: '12px', fontWeight: '600' }}>
              Branch:
            </label>
            <select
              id="branch-select"
              value={selectedBranch}
              onChange={(e) => {
                setSelectedBranch(e.target.value);
                setExpandedCompany(null);
              }}
              className="form-control"
              style={{ width: '180px' }}
            >
              <option value={ALL_BRANCHES_VALUE}>All Branches</option>
              {companies.map((company) => (
                <option key={company.id} value={String(company.id)}>
                  {company.name}
                </option>
              ))}
            </select>
          </>
        )}

        <label htmlFor="date-select" style={{ marginLeft: '20px', marginRight: '12px', fontWeight: '600' }}>
          View As of Date:
        </label>
        <input
          type="date"
          id="date-select"
          value={selectedDate}
          onChange={(e) => {
            setSelectedDate(e.target.value);
          }}
          className="form-control"
          style={{ width: '160px' }}
        />

        <label htmlFor="salesperson-filter" style={{ marginLeft: '20px', marginRight: '12px', fontWeight: '600' }}>
          Salesperson:
        </label>
        <select
          id="salesperson-filter"
          value={salespersonFilter}
          onChange={(e) => {
            setSalespersonFilter(e.target.value);
            setExpandedCompany(null);
          }}
          className="form-control"
          style={{ width: '280px' }}
        >
          <option value="">All Salespersons</option>
          {allSalespersons.map(sp => (
            <option key={sp.code} value={sp.name}>
              {sp.name} ({sp.code}) {sp.customer_count > 0 ? '- Active' : '- No invoices'}
            </option>
          ))}
        </select>

        <label htmlFor="salesperson-status" style={{ marginLeft: '20px', marginRight: '12px', fontWeight: '600' }}>
          Status:
        </label>
        <select
          id="salesperson-status"
          value={salespersonStatus}
          onChange={(e) => {
            setSalespersonStatus(e.target.value);
            setSalespersonFilter('');
            setExpandedCompany(null);
          }}
          className="form-control"
          style={{ width: '150px' }}
        >
          <option value="all">All</option>
          <option value="active">Active Only</option>
          <option value="inactive">Not Active Only</option>
        </select>
      </div>

      {/* Grand Totals Card */}
      <div className="grand-total-card summary-grand-total">
        <div className="summary-as-of">
          As of {new Date(selectedDate).toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
        <div className="grand-total-grid">
          <div className="grand-total-item">
            <span className="label">Total Customers</span>
            <span className="value">{grandTotals.customer_count}</span>
          </div>
          <div className="grand-total-item">
            <span className="label">Total Collectibles</span>
            <span className="value">{formatCurrency(grandTotals.total_collectibles)}</span>
          </div>
          <div className="grand-total-item">
            <span className="label">Posted Collected (ERP)</span>
            <span className="value">{formatCurrency(grandTotals.posted_collected)}</span>
          </div>
          <div className="grand-total-item">
            <span className="label">Amount Collected</span>
            <span className="value">{formatCurrency(grandTotals.amount_collected)}</span>
          </div>
          <div className="grand-total-item">
            <span className="label">PDC</span>
            <span className="value">{formatCurrency(grandTotals.pdc)}</span>
          </div>
          <div className="grand-total-item">
            <span className="label">Balance Collectibles</span>
            <span className="value">{formatCurrency(grandTotals.balance_collectible)}</span>
          </div>
          <div className="grand-total-item percentage">
            <span className="label">Collection %</span>
            <span className="value">{cePercentage}%</span>
          </div>
        </div>
      </div>

      {/* Salesperson Data by Company */}
      <div className="card ar-table-card">
        <div className="company-section">
          {companyArray.map((company, idx) => {
            const currentPage = companyPages[company.company_id] || 1;
            const totalSpPages = Math.ceil(company.salespersons.length / salespersonsPerPage);
            const startIdx = (currentPage - 1) * salespersonsPerPage;
            const endIdx = startIdx + salespersonsPerPage;
            const paginatedSalespersons = company.salespersons.slice(startIdx, endIdx);

            return (
              <div key={idx} className={`company-wrapper ${expandedCompany === company.company_id ? 'expanded' : ''}`}>
                <button
                  className="company-toggle-btn"
                  onClick={() => toggleCompany(company.company_id)}
                >
                  <span className="toggle-icon">{expandedCompany === company.company_id ? '-' : '+'}</span>
                  <strong>{company.name}</strong>
                  <span className="company-totals">
                    {company.salespersons.length} Salesperson | Total: {formatCurrency(company.totals.total_collectibles)}
                  </span>
                </button>

                {expandedCompany === company.company_id && (
                  <>
                    <table className="table ar-table salesperson-table table-compact">
                      <thead>
                        <tr>
                          <th className="sp-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Salesperson</th>
                          <th className="number-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Customers</th>
                          <th className="number-col number-highlight total-collectibles-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Total Collectible</th>
                          <th className="number-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Posted Collected (ERP)</th>
                          <th className="number-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Amount Collected</th>
                          <th className="number-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>PDC</th>
                          <th className="number-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Balance Collectibles</th>
                          <th className="percentage-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Collection %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedSalespersons.map(salesperson => {
                          const collectionPercent = salesperson.total_collectibles > 0
                            ? (((salesperson.amount_collected + salesperson.pdc) / salesperson.total_collectibles) * 100).toFixed(1)
                            : 0;

                          return (
                            <tr key={salesperson.salesperson_code}>
                              <td>
                                <strong>{salesperson.salesperson_name}</strong>
                                <br />
                                <span className="salesperson-code">{salesperson.salesperson_code}</span>
                              </td>
                              <td className="number-col">{salesperson.customer_count}</td>
                              <td className="number-col total-collectibles-col">{formatCurrency(salesperson.total_collectibles)}</td>
                              <td className="number-col">{formatCurrency(salesperson.posted_collected || 0)}</td>
                              <td className="number-col">{formatCurrency(salesperson.amount_collected || 0)}</td>
                              <td className="number-col">{formatCurrency(salesperson.pdc || 0)}</td>
                              <td className="number-col">{formatCurrency(salesperson.balance_collectible || 0)}</td>
                              <td className="percentage-col">{collectionPercent}%</td>
                            </tr>
                          );
                        })}

                        {/* Company Total Row */}
                        <tr className="total-row">
                          <td><strong>{company.name} TOTAL</strong></td>
                          <td className="number-col">{company.totals.customer_count}</td>
                          <td className="number-col total-collectibles-col">{formatCurrency(company.totals.total_collectibles)}</td>
                          <td className="number-col">{formatCurrency(company.totals.posted_collected)}</td>
                          <td className="number-col">{formatCurrency(company.totals.amount_collected)}</td>
                          <td className="number-col">{formatCurrency(company.totals.pdc)}</td>
                          <td className="number-col">{formatCurrency(company.totals.balance_collectible)}</td>
                          <td className="percentage-col">
                            {company.totals.total_collectibles > 0
                              ? (((company.totals.amount_collected + company.totals.pdc) / company.totals.total_collectibles) * 100).toFixed(1) + '%'
                              : '0%'}
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    {totalSpPages > 1 && (
                      <div className="pagination-controls">
                        <button
                          onClick={() => setCompanyPage(company.company_id, Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="btn btn-sm btn-primary"
                          style={{ backgroundColor: '#e63740', borderColor: '#e63740' }}
                        >
                          Previous
                        </button>

                        <span className="pagination-info">
                          Page {currentPage} of {totalSpPages} ({company.salespersons.length} salespersons)
                        </span>

                        <button
                          onClick={() => setCompanyPage(company.company_id, Math.min(totalSpPages, currentPage + 1))}
                          disabled={currentPage === totalSpPages}
                          className="btn btn-sm btn-primary"
                          style={{ backgroundColor: '#e63740', borderColor: '#e63740' }}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}

          {/* Grand Total Row */}
          <div className="company-wrapper grand-total-wrapper">
            <div className="company-toggle-btn grand-total-label">
              <strong>GRAND TOTAL</strong>
              <span className="company-totals">All Companies</span>
            </div>
            <table className="table ar-table salesperson-table table-compact">
              <tbody>
                <tr className="total-row">
                  <td><strong>ALL SALESPERSONS</strong></td>
                  <td className="number-col">{grandTotals.customer_count}</td>
                  <td className="number-col total-collectibles-col">{formatCurrency(grandTotals.total_collectibles)}</td>
                  <td className="number-col">{formatCurrency(grandTotals.posted_collected)}</td>
                  <td className="number-col">{formatCurrency(grandTotals.amount_collected)}</td>
                  <td className="number-col">{formatCurrency(grandTotals.pdc)}</td>
                  <td className="number-col">{formatCurrency(grandTotals.balance_collectible)}</td>
                  <td className="percentage-col">
                    {grandTotals.total_collectibles > 0
                      ? `${cePercentage}%`
                      : '0%'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SalespersonSummary;
